import React, { useCallback, useState, useMemo, useRef, useEffect, useLayoutEffect, useContext } from 'react'
import GLTransitions from 'gl-transitions'
import { Html } from 'react-konva-utils'
import { Layer, Image } from 'react-konva'
import { Surface } from 'gl-react-dom'
import { SliderTimeContext } from 'contexts/SliderTimeContext'
import GLTransition from '../../../../Util/shaders/GLTransition'
import Shaders from '../../shaders'
import { TRANSITIONS } from '../../../../enums'
import { animateLayer, excludeLayerAnimation } from '../lib'

/**
* TransitionRenderer.
* Updates state only when progress actually changes.
*/
const TransitionRenderer = React.memo(({
  asset, from, to, transition, transitionParams,
  animation, layerRef,
}) => {
  const { localTimeRef } = useContext(SliderTimeContext)
  const glInstanceRef = useRef(null)
  useEffect(() => {
    if (!animation || !layerRef.current) return undefined
    const originalFunc = animation.func
    // eslint-disable-next-line
    animation.func = async (frame) => {
      if (originalFunc) originalFunc(frame)
      const currentTime = localTimeRef.current
      if (glInstanceRef.current && asset.duration) {
        try {
          const pos = currentTime - asset.startTime
          const progress = Math.max(0, Math.min(1, pos / asset.duration))
          glInstanceRef.current.setProgress(progress)
        } catch (e) {
          console.error(`TransitionRenderer animation e: ${e}`)
        }
      }
    }
    // eslint-disable-next-line
    return () => { animation.func = originalFunc }
  }, [ animation, from, to, asset.duration,
    asset.startTime, localTimeRef, layerRef ])

  return (
    <GLTransition
      onConnectSizeComponentRef={instance => { glInstanceRef.current = instance }}
      from={from}
      to={to}
      progress={0}
      transition={transition}
      transitionParams={transitionParams}
    />
  )
})

const findCanvas = (id, layer) => (
  layer?.getStage().findOne(`#${id}`)?.getCanvas()._canvas ?? null
)

function setCanvasVisible(canvas, bVisible) {
  if (canvas) {
    // eslint-disable-next-line no-param-reassign
    canvas.style.visibility = bVisible ? 'initial' : 'hidden'
  }
}

const TransitionAssetOverlay = React.memo(({
  asset,
  index,
  boundingRect,
  animation,
  onRef,
}) => {
  const layer = useRef()
  const surface = useRef()
  const sliderContextValue = useContext(SliderTimeContext)

  const [ image, setImage ] = useState()
  const [ canvases, setCanvases ] = useState({ from: null, to: null })
  const [ force, setForce ] = useState(0)

  const { width, height } = boundingRect

  const transition = useMemo(() => {
    if (!asset.glslName) return null
    switch (asset.type) {
      case TRANSITIONS.BLUR:
      case TRANSITIONS.DIP_WHITE:
      case TRANSITIONS.DIP_BLACK:
        return Shaders.find(x => x.name === asset.glslName)
      default:
        return GLTransitions.find(x => x.name === asset.glslName)
    }
  }, [ asset.glslName, asset.type ])

  const transitionParams = useMemo(() => {
    if (asset.type === TRANSITIONS.BLUR) {
      return { resolution: [ width, height ] }
    }
    return asset.glslParams
  }, [ asset.type, asset.glslParams, width, height ])

  const useOnLoad = useCallback(() => {
    if (surface.current?.gl?.canvas) {
      setImage(surface.current.gl.canvas)
    }
  }, [])

  const isReady = useMemo(() => {
    const hasLeft = !asset.leftVideoAssetId || !!canvases.from
    const hasRight = !asset.rightVideoAssetId || !!canvases.to
    return hasLeft && hasRight
  }, [ asset.leftVideoAssetId, asset.rightVideoAssetId, canvases ])

  useEffect(() => {
    if (isReady) return undefined

    const interval = setInterval(() => {
      const layerCurrent = layer.current
      if (!layerCurrent) return

      const fromCanvas = asset.leftVideoAssetId
        ? findCanvas(asset.leftVideoAssetId, layerCurrent) : null
      const toCanvas = asset.rightVideoAssetId
        ? findCanvas(asset.rightVideoAssetId, layerCurrent) : null

      if (fromCanvas || toCanvas) {
        setCanvases(prev => {
          const nextFrom = fromCanvas || prev.from
          const nextTo = toCanvas || prev.to
          if (prev.from === nextFrom && prev.to === nextTo) return prev
          return { from: nextFrom, to: nextTo }
        })
      }
    }, 200)

    return () => clearInterval(interval)
  }, [ asset.leftVideoAssetId, asset.rightVideoAssetId, isReady ])

  // Hide the target video
  useLayoutEffect(() => {
    const layerCurrent = layer.current
    if (!layerCurrent || !asset.rightVideoAssetId) return undefined

    const toCanvas = findCanvas(asset.rightVideoAssetId, layerCurrent)
    if (toCanvas) {
      setCanvasVisible(toCanvas, false)
    }
    return () => {
      const canvas = findCanvas(asset.rightVideoAssetId, layerCurrent)
      if (canvas) setCanvasVisible(canvas, true)
    }
  }, [ asset.rightVideoAssetId, canvases.to, force ])

  // Hide the original video (when the shader is ready)
  useEffect(() => {
    if (!image) return undefined
    const layerCurrent = layer.current
    const fromCanvas = findCanvas(asset.leftVideoAssetId, layerCurrent)

    if (fromCanvas && !asset.isMountIn) {
      setCanvasVisible(fromCanvas, false)
    }

    return () => {
      const canvas = findCanvas(asset.leftVideoAssetId, layerCurrent)
      if (canvas) setCanvasVisible(canvas, true)
    }
  }, [ image, asset.leftVideoAssetId, asset.isMountIn ])

  // Setting up the Konva layer
  useEffect(() => {
    const layerCurrent = layer.current
    if (layerCurrent) {
      // eslint-disable-next-line no-underscore-dangle
      const nativeCanvas = layerCurrent.getCanvas()._canvas
      if (nativeCanvas) nativeCanvas.style.zIndex = index
      animateLayer(layerCurrent, animation)
    }
    return () => {
      if (layerCurrent) excludeLayerAnimation(layerCurrent, animation)
    }
  }, [ animation, index ])

  // External ref for forced update
  useEffect(() => {
    onRef && onRef(asset.id, { update: () => setForce(f => f + 1) })
    return () => onRef && onRef(asset.id, null)
  }, [ asset.id, onRef ])

  return (
    <Layer
      id={asset.id}
      name="transition"
      clip={boundingRect}
      listening={false}
      ref={layer}
    >
      {image && <Image image={image} width={width} height={height} />}

      <Html divProps={{ style: { visibility: 'hidden', lineHeight: 0, pointerEvents: 'none' } }}>
        <SliderTimeContext.Provider value={sliderContextValue}>
          {isReady && (
            <Surface
              key={`${asset.id}-${force}`}
              onLoad={useOnLoad}
              pixelRatio={1}
              webglContextAttributes={{
                preserveDrawingBuffer: true,
                premultipliedAlpha: false,
              }}
              ref={surface}
              width={width}
              height={height}
            >
              <TransitionRenderer
                asset={asset}
                from={canvases.from}
                to={canvases.to}
                transition={transition}
                transitionParams={transitionParams}
                animation={animation}
                layerRef={layer}
              />
            </Surface>
          )}
        </SliderTimeContext.Provider>
      </Html>
    </Layer>
  )
})

export default TransitionAssetOverlay
